import { reactive } from 'vue'
import ShopifyAPI from '@/api/shopify.js'

export const cartStore = reactive({
  cartId: null,
  items: [],
  loading: false,
  error: null,

  async createCart() {
    try {
      this.loading = true;
      this.error = null;
      
      const response = await ShopifyAPI.createCart([]);
      
      if (response.cartCreate.cart) {
        this.cartId = response.cartCreate.cart.id;
        this.items = response.cartCreate.cart.lines.edges.map(edge => edge.node);
        
        // Store cart ID in localStorage
        localStorage.setItem('shopifyCartId', this.cartId);
        
        return this.cartId;
      } else if (response.cartCreate.userErrors.length > 0) {
        throw new Error(response.cartCreate.userErrors[0].message);
      }
    } catch (error) {
      console.error('Error creating cart:', error);
      this.error = error.message;
      throw error;
    } finally {
      this.loading = false;
    }
  },

  async addToCart(variantId, quantity = 1) {
    try {
      this.loading = true;
      this.error = null;

      // Create cart if it doesn't exist
      if (!this.cartId) {
        await this.createCart();
      }

      const lines = [{
        merchandiseId: variantId,
        quantity: quantity
      }];

      const response = await ShopifyAPI.addToCart(this.cartId, lines);
      
      if (response.cartLinesAdd.cart) {
        this.items = response.cartLinesAdd.cart.lines.edges.map(edge => edge.node);
        this.cartId = response.cartLinesAdd.cart.id;
        
        return response.cartLinesAdd.cart;
      } else if (response.cartLinesAdd.userErrors.length > 0) {
        throw new Error(response.cartLinesAdd.userErrors[0].message);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.error = error.message;
      throw error;
    } finally {
      this.loading = false;
    }
  },

  async getCheckoutUrl() {
    if (!this.cartId) {
      throw new Error('No cart created');
    }
    
    try {
      const response = await ShopifyAPI.getCart(this.cartId);
      return response.cart.checkoutUrl;
    } catch (error) {
      console.error('Error getting checkout URL:', error);
      throw error;
    }
  },

  getItemCount() {
    return this.items.reduce((total, item) => total + item.quantity, 0);
  },

  getTotalPrice() {
    return this.items.reduce((total, item) => {
      const price = parseFloat(item.merchandise?.price?.amount || '0');
      return total + (price * item.quantity);
    }, 0);
  },

  async updateCartLine(lineId, quantity) {
    try {
      this.loading = true;
      this.error = null;

      if (!this.cartId) {
        throw new Error('No cart created');
      }

      const lines = [{
        id: lineId,
        quantity: quantity
      }];

      const response = await ShopifyAPI.updateCartLines(this.cartId, lines);
      
      if (response.cartLinesUpdate.cart) {
        this.items = response.cartLinesUpdate.cart.lines.edges.map(edge => edge.node);
        return response.cartLinesUpdate.cart;
      } else if (response.cartLinesUpdate.userErrors.length > 0) {
        throw new Error(response.cartLinesUpdate.userErrors[0].message);
      }
    } catch (error) {
      console.error('Error updating cart line:', error);
      this.error = error.message;
      throw error;
    } finally {
      this.loading = false;
    }
  },

  async removeCartLine(lineId) {
    try {
      this.loading = true;
      this.error = null;

      if (!this.cartId) {
        throw new Error('No cart created');
      }

      const response = await ShopifyAPI.removeCartLines(this.cartId, [lineId]);
      
      if (response.cartLinesRemove.cart) {
        this.items = response.cartLinesRemove.cart.lines.edges.map(edge => edge.node);
        return response.cartLinesRemove.cart;
      } else if (response.cartLinesRemove.userErrors.length > 0) {
        throw new Error(response.cartLinesRemove.userErrors[0].message);
      }
    } catch (error) {
      console.error('Error removing cart line:', error);
      this.error = error.message;
      throw error;
    } finally {
      this.loading = false;
    }
  },

  async refreshCartData() {
    if (!this.cartId) return;
    
    try {
      const response = await ShopifyAPI.getCart(this.cartId);
      if (response.cart) {
        this.items = response.cart.lines.edges.map(edge => edge.node);
      }
    } catch (error) {
      console.error('Error refreshing cart data:', error);
    }
  },

  // Initialize cart from localStorage on app start
  async initializeCart() {
    const savedCartId = localStorage.getItem('shopifyCartId');
    if (savedCartId) {
      this.cartId = savedCartId;
      try {
        const response = await ShopifyAPI.getCart(savedCartId);
        if (response.cart) {
          this.items = response.cart.lines.edges.map(edge => edge.node);
        }
      } catch (error) {
        // If cart doesn't exist anymore, clear it
        localStorage.removeItem('shopifyCartId');
        this.cartId = null;
        this.items = [];
      }
    }
  }
});